import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCart } from "@/lib/cart";
import { useCartTotals } from "@/lib/cart-totals";
import { Button } from "@/components/ui/button";
import {
  ShoppingCart,
  Trash2,
  ArrowLeft,
  ArrowRight,
  Zap,
  Upload,
  Plus,
  Minus,
  StickyNote,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useGangSheetBuilderScript } from "@/hooks/useGangSheetBuilderScript";
import { useFormatMoney } from "@/lib/currency";
import { supabase } from "@/integrations/supabase/client";
import { useRef } from "react";

export const Route = createFileRoute("/app/customer/cart")({ component: CartPage });

function CartPage() {
  const { items, removeItem, updateItem, clearCart } = useCart();
  const nav = useNavigate();
  const totals = useCartTotals(items);
  const fmt = useFormatMoney();
  const { openBuilder, status: builderStatus, error: builderError } = useGangSheetBuilderScript();
  const uploadInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const launchBuilder = async (itemId: string) => {
    const item = items.find((i) => i.id === itemId);
    if (!item) return;
    if (builderStatus === "checking" || builderStatus === "loading") {
      toast.info("Gang sheet builder is still loading — try again in a moment.");
      return;
    }
    if (builderStatus !== "ready") {
      toast.error(
        builderError ??
          "Gang sheet builder isn't available right now. Check Settings → Integrations.",
      );
      return;
    }
    try {
      await openBuilder({
        product_code: item.productCode,
        design_id: item.gangSheetLayoutId ?? undefined,
        onSaved: (designId, info) => {
          updateItem(itemId, {
            gangSheetLayoutId: designId,
            gangSheetPreviewUrl: info?.previewUrl ?? null,
            gangSheetSavedAt: info?.savedAt ?? new Date().toISOString(),
            uploadedArtworkUrl: null,
          });
          toast.success("Gang sheet saved");
        },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not open the gang sheet builder.");
    }
  };

  const handleUploadInstead = (itemId: string) => {
    uploadInputs.current[itemId]?.click();
  };

  const handleUploadFile = async (itemId: string, file: File) => {
    try {
      const path = `pending/${itemId}/${Date.now()}-${file.name}`;
      const { error: upErr } = await supabase.storage
        .from("job-artwork")
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("job-artwork").getPublicUrl(path);
      updateItem(itemId, {
        uploadedArtworkUrl: pub.publicUrl,
        uploadedArtworkName: file.name,
      });
      toast.success("Artwork uploaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShoppingCart className="h-6 w-6 sm:h-7 sm:w-7" /> Your cart
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {items.length === 0
              ? "No items yet — head to the shop to add products."
              : `${items.length} item${items.length === 1 ? "" : "s"} ready for checkout.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/customer/shop">
            <Button variant="outline" size="sm" className="rounded-full">
              <ArrowLeft className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Continue shopping</span>
            </Button>
          </Link>
          {items.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full text-destructive hover:bg-destructive/10"
              onClick={() => {
                if (confirm("Empty your cart? This cannot be undone.")) clearCart();
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-3xl border border-border bg-card p-12 text-center shadow-soft">
          <ShoppingCart className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
          <p className="font-medium">Your cart is empty</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Add a product from the shop to get started.
          </p>
          <Link to="/app/customer/shop">
            <Button className="mt-4 rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90">
              Browse shop
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_360px] items-start grid-cols-1">
          {/* Items */}
          <div className="space-y-3">
            {items.map((item) => {
              const line = totals.lines.find((l) => l.id === item.id);
              const lineTotal = line?.net ?? 0;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-3xl border border-border bg-card p-5 shadow-soft"
                >
                  <div className="flex gap-3 sm:gap-4">
                    {/* Image */}
                    <div className="h-16 w-16 sm:h-24 sm:w-24 shrink-0 overflow-hidden rounded-2xl bg-muted">
                      {item.productImage ? (
                        <img
                          src={item.productImage}
                          alt={item.productName}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="h-full w-full" />
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-semibold truncate">{item.productName}</h3>
                          {item.gangSheetName && (
                            <p
                              className="text-xs text-muted-foreground truncate"
                              title={item.gangSheetName}
                            >
                              <span className="font-medium text-foreground/80">Design:</span>{" "}
                              {item.gangSheetName}
                            </p>
                          )}
                          {item.variantLabel && (
                            <p className="text-xs text-muted-foreground">{item.variantLabel}</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {fmt(item.pricePerMetre)} / metre · {item.metres}m
                          </p>
                          {item.priceModifier ? (
                            <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
                              Includes {item.priceModifier > 0 ? "+" : ""}
                              {fmt(item.priceModifier)}/m for{" "}
                              {item.variantLabel ?? "selected option"}
                              {item.basePricePerMetre
                                ? ` (base ${fmt(item.basePricePerMetre)}/m)`
                                : ""}
                            </p>
                          ) : null}
                        </div>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-muted-foreground hover:text-destructive shrink-0"
                          aria-label="Remove from cart"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Quantity + price */}
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-background">
                          <button
                            onClick={() =>
                              updateItem(item.id, { quantity: Math.max(1, item.quantity - 1) })
                            }
                            className="p-1.5 hover:bg-muted rounded-l-full"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="px-3 text-sm font-medium tabular-nums">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateItem(item.id, { quantity: item.quantity + 1 })}
                            className="p-1.5 hover:bg-muted rounded-r-full"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <span className="text-base font-semibold tabular-nums">
                          {fmt(lineTotal)}
                        </span>
                      </div>

                      {/* Notes */}
                      <details className="text-sm">
                        <summary className="cursor-pointer text-xs text-muted-foreground inline-flex items-center gap-1.5 hover:text-foreground">
                          <StickyNote className="h-3 w-3" />
                          {item.notes ? "Edit notes" : "Add notes"}
                        </summary>
                        <textarea
                          value={item.notes ?? ""}
                          onChange={(e) => updateItem(item.id, { notes: e.target.value })}
                          placeholder="Any special instructions for this item…"
                          className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm min-h-[60px]"
                        />
                      </details>

                      {/* Gang sheet status / actions */}
                      {item.requiresGangSheet && (
                        <div className="mt-2 rounded-2xl border border-amber-200/60 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900/40 p-3 space-y-2">
                          <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
                            {item.gangSheetLayoutId
                              ? "✓ Gang sheet ready"
                              : item.uploadedArtworkUrl
                                ? "✓ Artwork uploaded"
                                : "Action needed: prepare your gang sheet"}
                          </p>
                          {item.gangSheetLayoutId && (
                            <div className="flex items-start gap-2 sm:gap-3">
                              <div className="h-12 w-12 sm:h-16 sm:w-16 shrink-0 overflow-hidden rounded-lg border border-border bg-background">
                                {item.gangSheetPreviewUrl ? (
                                  <img
                                    src={item.gangSheetPreviewUrl}
                                    alt="Gang sheet preview"
                                    className="h-full w-full object-contain"
                                    onError={(e) => {
                                      (e.currentTarget as HTMLImageElement).style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                                    <Zap className="h-5 w-5" />
                                  </div>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground space-y-0.5 min-w-0">
                                <div className="font-mono text-foreground/80 truncate max-w-[140px] sm:max-w-[180px]">
                                  #{item.gangSheetLayoutId}
                                </div>
                                {item.gangSheetSavedAt && (
                                  <div>
                                    Saved{" "}
                                    {new Date(item.gangSheetSavedAt).toLocaleString(undefined, {
                                      dateStyle: "medium",
                                      timeStyle: "short",
                                    })}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="text-primary hover:underline"
                                  onClick={() => launchBuilder(item.id)}
                                >
                                  Edit gang sheet
                                </button>
                              </div>
                            </div>
                          )}
                          {!item.gangSheetLayoutId && !item.uploadedArtworkUrl && (
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
                                onClick={() => launchBuilder(item.id)}
                              >
                                <Zap className="h-3.5 w-3.5 mr-1.5" /> Build gang sheet
                              </Button>
                              {item.allowManualUpload && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-full"
                                    onClick={() => handleUploadInstead(item.id)}
                                  >
                                    <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload my own
                                  </Button>
                                  <input
                                    ref={(el) => {
                                      uploadInputs.current[item.id] = el;
                                    }}
                                    type="file"
                                    accept="image/*,application/pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) handleUploadFile(item.id, f);
                                      e.target.value = "";
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          )}
                          {item.uploadedArtworkUrl && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Upload className="h-3 w-3" />
                              <a
                                href={item.uploadedArtworkUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary hover:underline truncate max-w-[220px]"
                              >
                                {item.uploadedArtworkName ?? "Uploaded artwork"}
                              </a>
                              <button
                                type="button"
                                className="text-destructive hover:underline"
                                onClick={() =>
                                  updateItem(item.id, {
                                    uploadedArtworkUrl: null,
                                    uploadedArtworkName: null,
                                  })
                                }
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Summary */}
          <aside className="space-y-4 lg:sticky lg:top-6 order-first lg:order-last">
            <div className="rounded-3xl border border-border bg-card p-6 shadow-soft space-y-4">
              <h2 className="text-lg font-semibold">Order summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold tabular-nums">{fmt(totals.subtotal)}</span>
                </div>
                {totals.taxEnabled && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {totals.taxLabel}
                      {totals.taxRateLabel}
                    </span>
                    <span className="font-semibold tabular-nums">{fmt(totals.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between text-muted-foreground">
                  <span>Shipping</span>
                  <span>At checkout</span>
                </div>
              </div>
              <div className="border-t border-border pt-3 flex justify-between text-base font-semibold">
                <span>Estimated total</span>
                <span className="tabular-nums">{fmt(totals.total)}</span>
              </div>
              <Button
                className="w-full rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90 gap-2"
                onClick={() => nav({ to: "/app/customer/checkout" })}
              >
                Proceed to checkout <ArrowRight className="h-4 w-4" />
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                Final pricing, shipping, and any required artwork are confirmed at checkout.
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
